import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import { userManageAction } from 'actions';
import Card from 'components/Card';
import CardBody from 'components/Card/CardBody';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

const ChangePasswordForm = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { loadingBtnChangePw } = useSelector((state) => state.userManage);

  const [showOldPw, setShowOldPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmNewPw, setShowConfirmNewPw] = useState(false);

  const validateOldPassword = useCallback(
    (value: string) => {
      let error;
      if ((value || '').trim().length === 0) {
        error = t('changePassword.oldPasswordRequired');
      }
      return error;
    },
    [i18n.language, t]
  );

  const validateNewPassword = useCallback(
    (value: string) => {
      let error;
      if ((value || '').trim().length === 0) {
        error = t('changePassword.newPasswordRequired');
      }
      return error;
    },
    [i18n.language, t]
  );

  const validateConfirmPassword = useCallback(
    (newPassword: string, value: string) => {
      let textErr;
      if (value !== newPassword) {
        textErr = t('changePassword.newPasswordNotMatch');
      }
      return textErr;
    },
    [i18n.language, t]
  );

  return (
    <Box>
      <Heading as='h3' size='md'>
        {t('changePassword.changePassword')}
      </Heading>
      <Box w={{ base: '100%', md: '50%' }} m={'auto'} mt={'1.5rem'} mb='2rem' border={'0.5px'}>
        <Card bg='bg_box' border='none'>
          <CardBody>
            <Box p={'50px 25px'}>
              <Formik
                i18nIsDynamicList
                initialValues={{
                  confirmNewPwd: '',
                  oldPassword: '',
                  newPassword: '',
                }}
                onSubmit={(values, formProps) => {
                  dispatch(userManageAction.changePassword(values));
                  formProps.resetForm();
                }}
              >
                {({ errors, touched, values, resetForm }) => {
                  i18n.on('languageChanged', (lng) => {
                    resetForm();
                  });
                  return (
                    <>
                      <Form id='formFormikPw'>
                        <Field name='oldPassword' validate={validateOldPassword}>
                          {({ field }: FieldProps) => (
                            <FormControl
                              mb='1.5rem'
                              isInvalid={!!errors.oldPassword && touched.oldPassword}
                            >
                              <FormLabel> {t('changePassword.oldPassword')}</FormLabel>
                              <InputGroup>
                                <Input
                                  type={showOldPw ? 'text' : 'password'}
                                  {...field}
                                  id='oldPassword'
                                  placeholder=''
                                />
                                <InputRightElement
                                  cursor={'pointer'}
                                  onClick={() => setShowOldPw(!showOldPw)}
                                >
                                  {showOldPw ? <ViewIcon /> : <ViewOffIcon />}
                                </InputRightElement>
                              </InputGroup>
                              <FormErrorMessage>{errors.oldPassword}</FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                        <Field id='newPassword' name='newPassword' validate={validateNewPassword}>
                          {({ field }: FieldProps) => (
                            <FormControl
                              mb='1.5rem'
                              isInvalid={!!errors.newPassword && touched.newPassword}
                            >
                              <FormLabel>{t('changePassword.newPassword')}</FormLabel>
                              <InputGroup>
                                <Input
                                  type={showNewPw ? 'text' : 'password'}
                                  {...field}
                                  id='newPassword'
                                  placeholder=''
                                />
                                <InputRightElement
                                  cursor={'pointer'}
                                  onClick={() => setShowNewPw(!showNewPw)}
                                >
                                  {showNewPw ? <ViewIcon /> : <ViewOffIcon />}
                                </InputRightElement>
                              </InputGroup>
                              <FormErrorMessage>{errors.newPassword}</FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                        <Field
                          name='confirmNewPwd'
                          validate={(value: string) =>
                            validateConfirmPassword(values.newPassword, value)
                          }
                        >
                          {({ field }: FieldProps) => (
                            <FormControl
                              mb='1.5rem'
                              isInvalid={!!(errors.confirmNewPwd && touched.confirmNewPwd)}
                            >
                              <FormLabel>{t('changePassword.confirmNewPassword')}</FormLabel>
                              <InputGroup>
                                <Input
                                  type={showConfirmNewPw ? 'text' : 'password'}
                                  {...field}
                                  id='confirmNewPwd'
                                  placeholder=''
                                />
                                <InputRightElement
                                  cursor={'pointer'}
                                  onClick={() => setShowConfirmNewPw(!showConfirmNewPw)}
                                >
                                  {showConfirmNewPw ? <ViewIcon /> : <ViewOffIcon />}
                                </InputRightElement>
                              </InputGroup>
                              <FormErrorMessage>{errors.confirmNewPwd}</FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                      </Form>
                      <Box mt={10} textAlign={'end'}>
                        <Button
                          ml={3}
                          form='formFormikPw'
                          isDisabled={
                            !values.newPassword ||
                            !values.oldPassword ||
                            !!errors.newPassword ||
                            !!errors.oldPassword ||
                            !!errors.confirmNewPwd
                            // !values.totp
                          }
                          isLoading={loadingBtnChangePw}
                          loadingText='Changing'
                          type='submit'
                          variant='amatrium_submit'
                        >
                          {t('common.change')}
                        </Button>
                      </Box>
                    </>
                  );
                }}
              </Formik>
            </Box>
          </CardBody>
        </Card>
      </Box>
    </Box>
  );
};
export default ChangePasswordForm;
